import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, DialogContentText, Pagination,
  CircularProgress, TextField, Grid, TableSortLabel, Collapse, Card,
  CardContent, Autocomplete, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  FilterList as FilterIcon, Clear as ClearIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../../services/api';
import Layout from '../Layout';

// ========== Aba Historico ==========

const AbaHistorico = ({ responsaveis, empresas }) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editingHistorico, setEditingHistorico] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({ pagina_atual: 1, total_paginas: 1, total_registros: 0, registros_por_pagina: 50 });
  const [filtros, setFiltros] = useState({ empresa: '', responsavel: '', data_inicio_de: null, data_inicio_ate: null, data_fim_de: null, data_fim_ate: null });
  const [ordenacao, setOrdenacao] = useState({ order_by: 'ID', order_direction: 'asc' });
  const [formData, setFormData] = useState({ IDEmpresa: '', IDResponsavel: '', DataInicio: new Date(), DataFim: null, empresaSelecionada: null, responsavelSelecionado: null });

  const fetchHistorico = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      const ord = customOrdenacao || ordenacao;
      const params = new URLSearchParams({ page: page.toString(), limit: '50', order_by: ord.order_by, order_direction: ord.order_direction });
      if (filtros.empresa) params.append('empresa', filtros.empresa);
      if (filtros.responsavel) params.append('responsavel', filtros.responsavel);
      if (filtros.data_inicio_de instanceof Date) params.append('data_inicio_de', filtros.data_inicio_de.toISOString().split('T')[0]);
      if (filtros.data_inicio_ate instanceof Date) params.append('data_inicio_ate', filtros.data_inicio_ate.toISOString().split('T')[0]);
      if (filtros.data_fim_de instanceof Date) params.append('data_fim_de', filtros.data_fim_de.toISOString().split('T')[0]);
      if (filtros.data_fim_ate instanceof Date) params.append('data_fim_ate', filtros.data_fim_ate.toISOString().split('T')[0]);
      const response = await api.get(`/historico-responsaveis?${params}`);
      if (response.data.dados && response.data.paginacao) {
        setHistorico(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        setHistorico(response.data);
        setPaginacao({ pagina_atual: 1, total_paginas: 1, total_registros: response.data.length, registros_por_pagina: response.data.length });
      }
    } catch { setError('Erro ao carregar histórico.'); }
    finally { setLoading(false); }
  }, [filtros, ordenacao]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHistorico(1); }, []);

  const handleSort = (campo) => {
    const nova = { order_by: campo, order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc' };
    setOrdenacao(nova);
    fetchHistorico(1, nova);
  };

  const handleFilterChange = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }));

  const limparFiltros = () => {
    setFiltros({ empresa: '', responsavel: '', data_inicio_de: null, data_inicio_ate: null, data_fim_de: null, data_fim_ate: null });
    setTimeout(() => fetchHistorico(1), 100);
  };

  const resetForm = () => setFormData({ IDEmpresa: '', IDResponsavel: '', DataInicio: new Date(), DataFim: null, empresaSelecionada: null, responsavelSelecionado: null });

  const handleSubmit = async () => {
    try {
      setDialogError('');
      const dadosParaEnvio = {
        IDEmpresa: formData.IDEmpresa,
        IDResponsavel: formData.IDResponsavel,
        DataInicio: formData.DataInicio ? formData.DataInicio.toISOString().split('T')[0] : null,
        DataFim: formData.DataFim ? formData.DataFim.toISOString().split('T')[0] : null,
      };
      if (editingHistorico) {
        const res = await api.put(`/historico-responsaveis/${editingHistorico.ID}`, dadosParaEnvio);
        const bi = res.data?.resultados_bi_atualizados;
        setSuccess(`Histórico atualizado com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      } else {
        const res = await api.post('/historico-responsaveis', dadosParaEnvio);
        const bi = res.data?.resultados_bi_atualizados;
        setSuccess(`Histórico adicionado com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      }
      setOpenDialog(false); setEditingHistorico(null); resetForm();
      fetchHistorico(paginacao.pagina_atual);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setDialogError(err.response?.data?.message || 'Erro ao salvar.');
    }
  };

  const handleEdit = (reg) => {
    const empresaObj = empresas.find(e => e.ID === reg.IDEmpresa) || null;
    const responsavelObj = responsaveis.find(r => r.id === reg.IDResponsavel) || null;
    setEditingHistorico(reg);
    setFormData({ IDEmpresa: reg.IDEmpresa, IDResponsavel: reg.IDResponsavel, DataInicio: new Date(reg.DataInicio), DataFim: reg.DataFim ? new Date(reg.DataFim) : null, empresaSelecionada: empresaObj, responsavelSelecionado: responsavelObj });
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await api.delete(`/historico-responsaveis/${editingHistorico.ID}`);
      const bi = res.data?.resultados_bi_atualizados;
      setSuccess(`Registro excluído com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      fetchHistorico(paginacao.pagina_atual);
      setOpenConfirmDialog(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Erro ao excluir.'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '';

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Barra de ações */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant={showFilters ? "contained" : "outlined"} size="small" onClick={() => setShowFilters(!showFilters)} startIcon={<FilterIcon />}>Filtros</Button>
        </Box>
        <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => { setEditingHistorico(null); resetForm(); setDialogError(''); setOpenDialog(true); }}>Novo Registro</Button>
      </Box>

      {/* Filtros */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Empresa" value={filtros.empresa} onChange={(e) => handleFilterChange('empresa', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth size="small" label="Responsável" value={filtros.responsavel} onChange={(e) => handleFilterChange('responsavel', e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker label="Início De" value={filtros.data_inicio_de} onChange={(v) => handleFilterChange('data_inicio_de', v)} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker label="Início Até" value={filtros.data_inicio_ate} onChange={(v) => handleFilterChange('data_inicio_ate', v)} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={() => fetchHistorico(1)}>Filtrar</Button>
                  <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={limparFiltros}>Limpar</Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Tabela */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'ID'} direction={ordenacao.order_by === 'ID' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('ID')}>ID</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'NomeFantasia'} direction={ordenacao.order_by === 'NomeFantasia' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('NomeFantasia')}>Empresa</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'NomeResponsavel'} direction={ordenacao.order_by === 'NomeResponsavel' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('NomeResponsavel')}>Comercial</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'DataInicio'} direction={ordenacao.order_by === 'DataInicio' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('DataInicio')}>Data Início</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'DataFim'} direction={ordenacao.order_by === 'DataFim' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('DataFim')}>Data Fim</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={30} /></TableCell></TableRow>
              ) : historico.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum registro encontrado</TableCell></TableRow>
              ) : historico.map((reg) => (
                <TableRow key={reg.ID} hover>
                  <TableCell>{reg.ID}</TableCell>
                  <TableCell>{reg.NomeFantasia}</TableCell>
                  <TableCell>{reg.NomeResponsavel}</TableCell>
                  <TableCell>{formatDate(reg.DataInicio)}</TableCell>
                  <TableCell>{formatDate(reg.DataFim)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(reg)} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => { setEditingHistorico(reg); setOpenConfirmDialog(true); }} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {paginacao.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="caption" color="text.secondary">{paginacao.total_registros} registros</Typography>
            <Pagination count={paginacao.total_paginas} page={paginacao.pagina_atual} onChange={(_, p) => fetchHistorico(p)} color="primary" size="small" />
          </Box>
        )}
        {paginacao.total_paginas <= 1 && paginacao.total_registros > 0 && (
          <Box sx={{ p: 1, textAlign: 'right' }}><Typography variant="caption" color="text.secondary">{paginacao.total_registros} registros</Typography></Box>
        )}
      </Paper>

      {/* Dialog Criar/Editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingHistorico ? 'Editar Registro' : 'Novo Registro'}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>{dialogError}</Alert>}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              fullWidth options={empresas}
              getOptionLabel={(o) => { const id = o?.ID != null ? String(o.ID) : ''; const n = o?.NomeFantasia ?? ''; return id ? `${id} - ${n}` : n; }}
              value={formData.empresaSelecionada}
              onChange={(_, v) => setFormData({ ...formData, IDEmpresa: v ? v.ID : '', empresaSelecionada: v })}
              isOptionEqualToValue={(o, v) => o.ID === v.ID}
              filterOptions={(opts, { inputValue }) => { const t = inputValue?.toLowerCase() ?? ''; return opts.filter(o => (o?.NomeFantasia?.toLowerCase() ?? '').includes(t) || String(o?.ID ?? '').includes(inputValue)); }}
              renderInput={(params) => <TextField {...params} label="Empresa" placeholder="Digite para buscar..." required />}
              noOptionsText="Nenhuma empresa encontrada"
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
            <Autocomplete
              fullWidth options={responsaveis}
              getOptionLabel={(o) => o.Responsavel || ''}
              value={formData.responsavelSelecionado}
              onChange={(_, v) => setFormData({ ...formData, IDResponsavel: v ? v.id : '', responsavelSelecionado: v })}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterOptions={(opts, { inputValue }) => opts.filter(o => o.Responsavel.toLowerCase().includes(inputValue.toLowerCase()))}
              renderInput={(params) => <TextField {...params} label="Comercial" placeholder="Digite para buscar..." required />}
              noOptionsText="Nenhum responsável encontrado"
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker label="Data Início" value={formData.DataInicio} onChange={(v) => setFormData({ ...formData, DataInicio: v })} slotProps={{ textField: { fullWidth: true, required: true } }} />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker label="Data Fim (opcional)" value={formData.DataFim} onChange={(v) => setFormData({ ...formData, DataFim: v })} slotProps={{ textField: { fullWidth: true } }} />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setDialogError(''); }} color="inherit">Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação exclusão */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent><DialogContentText>Tem certeza que deseja excluir este registro?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


// ========== Aba Substituicoes ==========

const AbaSubstituicoes = ({ responsaveis, empresasAtivas }) => {
  const [substituicoes, setSubstituicoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editingSubstituicao, setEditingSubstituicao] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({ pagina_atual: 1, total_paginas: 1, total_registros: 0, registros_por_pagina: 50 });
  const [filtros, setFiltros] = useState({ empresa: '', responsavel_principal: '', responsavel_substituto: '', data_inicio_de: null, data_inicio_ate: null, data_fim_de: null, data_fim_ate: null });
  const [ordenacao, setOrdenacao] = useState({ order_by: 'ID', order_direction: 'asc' });
  const [formData, setFormData] = useState({ IDEmpresa: '', IDResponsavelSubstituto: '', DataInicio: new Date(), DataFim: null, empresaSelecionada: null, responsavelSubstitutoSelecionado: null });

  const fetchSubstituicoes = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      const ord = customOrdenacao || ordenacao;
      const params = new URLSearchParams({ page: page.toString(), limit: '50', order_by: ord.order_by, order_direction: ord.order_direction });
      if (filtros.empresa) params.append('empresa', filtros.empresa);
      if (filtros.responsavel_principal) params.append('responsavel_principal', filtros.responsavel_principal);
      if (filtros.responsavel_substituto) params.append('responsavel_substituto', filtros.responsavel_substituto);
      if (filtros.data_inicio_de instanceof Date) params.append('data_inicio_de', filtros.data_inicio_de.toISOString().split('T')[0]);
      if (filtros.data_inicio_ate instanceof Date) params.append('data_inicio_ate', filtros.data_inicio_ate.toISOString().split('T')[0]);
      if (filtros.data_fim_de instanceof Date) params.append('data_fim_de', filtros.data_fim_de.toISOString().split('T')[0]);
      if (filtros.data_fim_ate instanceof Date) params.append('data_fim_ate', filtros.data_fim_ate.toISOString().split('T')[0]);
      const response = await api.get(`/substituicoes-responsaveis?${params}`);
      if (response.data.dados && response.data.paginacao) {
        setSubstituicoes(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        setSubstituicoes(response.data);
        setPaginacao({ pagina_atual: 1, total_paginas: 1, total_registros: response.data.length, registros_por_pagina: response.data.length });
      }
    } catch { setError('Erro ao carregar substituições.'); }
    finally { setLoading(false); }
  }, [filtros, ordenacao]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSubstituicoes(1); }, []);

  const handleSort = (campo) => {
    const nova = { order_by: campo, order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc' };
    setOrdenacao(nova);
    fetchSubstituicoes(1, nova);
  };

  const handleFilterChange = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }));

  const limparFiltros = () => {
    setFiltros({ empresa: '', responsavel_principal: '', responsavel_substituto: '', data_inicio_de: null, data_inicio_ate: null, data_fim_de: null, data_fim_ate: null });
    setTimeout(() => fetchSubstituicoes(1), 100);
  };

  const resetForm = () => setFormData({ IDEmpresa: '', IDResponsavelSubstituto: '', DataInicio: new Date(), DataFim: null, empresaSelecionada: null, responsavelSubstitutoSelecionado: null });

  const handleSubmit = async () => {
    try {
      setDialogError('');
      if (!formData.IDEmpresa || !formData.IDResponsavelSubstituto || !formData.DataInicio || !formData.DataFim) {
        setDialogError('Todos os campos são obrigatórios.');
        return;
      }
      if (formData.DataFim <= formData.DataInicio) {
        setDialogError('A data de fim deve ser posterior à data de início.');
        return;
      }
      const dadosParaEnvio = {
        IDEmpresa: formData.IDEmpresa,
        IDResponsavelSubstituto: formData.IDResponsavelSubstituto,
        DataInicio: formData.DataInicio ? formData.DataInicio.toISOString().split('T')[0] : null,
        DataFim: formData.DataFim ? formData.DataFim.toISOString().split('T')[0] : null,
      };
      if (editingSubstituicao) {
        const res = await api.put(`/substituicoes-responsaveis/${editingSubstituicao.ID}`, dadosParaEnvio);
        const bi = res.data?.resultados_bi_atualizados;
        setSuccess(`Substituição atualizada com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      } else {
        const res = await api.post('/substituicoes-responsaveis', dadosParaEnvio);
        const bi = res.data?.resultados_bi_atualizados;
        setSuccess(`Substituição adicionada com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      }
      setOpenDialog(false); setEditingSubstituicao(null); resetForm();
      fetchSubstituicoes(paginacao.pagina_atual);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setDialogError(err.response?.data?.message || 'Erro ao salvar.');
    }
  };

  const handleEdit = (reg) => {
    const empresaObj = empresasAtivas.find(e => e.ID === reg.IDEmpresa) || null;
    const responsavelObj = responsaveis.find(r => r.id === reg.IDResponsavelSubstituto) || null;
    setEditingSubstituicao(reg);
    setFormData({ IDEmpresa: reg.IDEmpresa, IDResponsavelSubstituto: reg.IDResponsavelSubstituto, DataInicio: new Date(reg.DataInicio), DataFim: reg.DataFim ? new Date(reg.DataFim) : null, empresaSelecionada: empresaObj, responsavelSubstitutoSelecionado: responsavelObj });
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await api.delete(`/substituicoes-responsaveis/${editingSubstituicao.ID}`);
      const bi = res.data?.resultados_bi_atualizados;
      setSuccess(`Registro excluído com sucesso!${bi ? ` ${bi} registro(s) do Resultados BI atualizados.` : ''}`);
      fetchSubstituicoes(paginacao.pagina_atual);
      setOpenConfirmDialog(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Erro ao excluir.'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '';

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Barra de ações */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant={showFilters ? "contained" : "outlined"} size="small" onClick={() => setShowFilters(!showFilters)} startIcon={<FilterIcon />}>Filtros</Button>
        </Box>
        <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => { setEditingSubstituicao(null); resetForm(); setDialogError(''); setOpenDialog(true); }}>Nova Substituição</Button>
      </Box>

      {/* Filtros */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <TextField fullWidth size="small" label="Empresa" value={filtros.empresa} onChange={(e) => handleFilterChange('empresa', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField fullWidth size="small" label="Responsável Principal" value={filtros.responsavel_principal} onChange={(e) => handleFilterChange('responsavel_principal', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField fullWidth size="small" label="Substituto" value={filtros.responsavel_substituto} onChange={(e) => handleFilterChange('responsavel_substituto', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={() => fetchSubstituicoes(1)}>Filtrar</Button>
                  <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={limparFiltros}>Limpar</Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Tabela */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'ID'} direction={ordenacao.order_by === 'ID' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('ID')}>ID</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'NomeFantasia'} direction={ordenacao.order_by === 'NomeFantasia' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('NomeFantasia')}>Empresa</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'ResponsavelPrincipal'} direction={ordenacao.order_by === 'ResponsavelPrincipal' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('ResponsavelPrincipal')}>Comercial Principal</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'ResponsavelSubstituto'} direction={ordenacao.order_by === 'ResponsavelSubstituto' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('ResponsavelSubstituto')}>Substituto</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'DataInicio'} direction={ordenacao.order_by === 'DataInicio' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('DataInicio')}>Data Início</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}><TableSortLabel active={ordenacao.order_by === 'DataFim'} direction={ordenacao.order_by === 'DataFim' ? ordenacao.order_direction : 'asc'} onClick={() => handleSort('DataFim')}>Data Fim</TableSortLabel></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={30} /></TableCell></TableRow>
              ) : substituicoes.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum registro encontrado</TableCell></TableRow>
              ) : substituicoes.map((reg) => (
                <TableRow key={reg.ID} hover>
                  <TableCell>{reg.ID}</TableCell>
                  <TableCell>{reg.NomeFantasia}</TableCell>
                  <TableCell>{reg.ResponsavelPrincipal}</TableCell>
                  <TableCell>{reg.ResponsavelSubstituto}</TableCell>
                  <TableCell>{formatDate(reg.DataInicio)}</TableCell>
                  <TableCell>{formatDate(reg.DataFim)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(reg)} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => { setEditingSubstituicao(reg); setOpenConfirmDialog(true); }} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {paginacao.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="caption" color="text.secondary">{paginacao.total_registros} registros</Typography>
            <Pagination count={paginacao.total_paginas} page={paginacao.pagina_atual} onChange={(_, p) => fetchSubstituicoes(p)} color="primary" size="small" />
          </Box>
        )}
        {paginacao.total_paginas <= 1 && paginacao.total_registros > 0 && (
          <Box sx={{ p: 1, textAlign: 'right' }}><Typography variant="caption" color="text.secondary">{paginacao.total_registros} registros</Typography></Box>
        )}
      </Paper>

      {/* Dialog Criar/Editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSubstituicao ? 'Editar Substituição' : 'Nova Substituição'}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>{dialogError}</Alert>}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              fullWidth options={empresasAtivas}
              getOptionLabel={(o) => o.NomeFantasia || ''}
              value={formData.empresaSelecionada}
              onChange={(_, v) => setFormData({ ...formData, IDEmpresa: v ? v.ID : '', empresaSelecionada: v })}
              isOptionEqualToValue={(o, v) => o.ID === v.ID}
              filterOptions={(opts, { inputValue }) => opts.filter(o => o.NomeFantasia.toLowerCase().includes(inputValue.toLowerCase()))}
              renderInput={(params) => <TextField {...params} label="Empresa" placeholder="Digite para buscar..." required />}
              noOptionsText="Nenhuma empresa encontrada"
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
            <Autocomplete
              fullWidth options={responsaveis}
              getOptionLabel={(o) => o.Responsavel || ''}
              value={formData.responsavelSubstitutoSelecionado}
              onChange={(_, v) => setFormData({ ...formData, IDResponsavelSubstituto: v ? v.id : '', responsavelSubstitutoSelecionado: v })}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterOptions={(opts, { inputValue }) => opts.filter(o => o.Responsavel.toLowerCase().includes(inputValue.toLowerCase()))}
              renderInput={(params) => <TextField {...params} label="Substituto" placeholder="Digite para buscar..." required />}
              noOptionsText="Nenhum responsável encontrado"
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker label="Data de Início" value={formData.DataInicio} onChange={(v) => setFormData({ ...formData, DataInicio: v })} slotProps={{ textField: { fullWidth: true, required: true } }} />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker label="Data de Fim" value={formData.DataFim} onChange={(v) => setFormData({ ...formData, DataFim: v })} slotProps={{ textField: { fullWidth: true, required: true } }} />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setDialogError(''); }} color="inherit">Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação exclusão */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent><DialogContentText>Tem certeza que deseja excluir esta substituição?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


// ========== Componente Principal ==========

const ComerciaisCorretorasPanel = () => {
  const [tab, setTab] = useState(0);
  const [responsaveis, setResponsaveis] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresasAtivas, setEmpresasAtivas] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { window.location.href = '/login'; return; }

    api.get('/responsaveis').then(res => setResponsaveis(res.data)).catch(() => {});
    api.get('/empresas').then(res => {
      const d = res.data?.empresas || res.data || [];
      const norm = Array.isArray(d) ? d.map(e => ({ ID: e.ID ?? e.id, NomeFantasia: e.NomeFantasia ?? e['Nome Fantasia'] ?? e.nomeFantasia ?? e.razao_social ?? e.nome ?? '' })) : [];
      setEmpresas(norm);
    }).catch(() => {});
    api.get('/empresas-ativas').then(res => setEmpresasAtivas(res.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <Box sx={{ py: 3, px: 2 }}>
        <Typography variant="h5" gutterBottom>Comercial por Corretora</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Gerencie o vínculo de comerciais para empresas (corretoras) que usam o modelo "comercial por corretora"
        </Typography>

        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
            <Tab label="Histórico de Comerciais" />
            <Tab label="Comercial Temporário" />
          </Tabs>
        </Paper>

        {tab === 0 && <AbaHistorico responsaveis={responsaveis} empresas={empresas} />}
        {tab === 1 && <AbaSubstituicoes responsaveis={responsaveis} empresasAtivas={empresasAtivas} />}
      </Box>
    </Layout>
  );
};

export default ComerciaisCorretorasPanel;
